import { useState } from 'react';
import { X, CheckCircle, XCircle, Copy, Check, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import type { ExecutionLog } from '../../types/executionLog';
import { executionLogService } from '../../services/executionLogService';

interface ExecutionLogDetailModalProps {
  log: ExecutionLog;
  onClose: () => void;
}

export default function ExecutionLogDetailModal({ log, onClose }: ExecutionLogDetailModalProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'request' | 'response'>('request');

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatJson = (data: Record<string, unknown>): string => {
    return JSON.stringify(data, null, 2);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Detalhes da Execucao</h2>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${executionLogService.getTriggerTypeColor(log.trigger_type)}`}>
              {executionLogService.getTriggerTypeLabel(log.trigger_type)}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          <div className="px-6 py-4 border-b border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Data/Hora</p>
              <p className="text-sm font-medium text-gray-900">
                {executionLogService.formatDate(log.created_at)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Status</p>
              <div className="flex items-center gap-1.5">
                {log.success ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium text-green-600">Sucesso</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-medium text-red-600">Erro</span>
                  </>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">HTTP Status</p>
              <p className={`text-sm font-mono font-medium ${
                log.response_status && log.response_status >= 200 && log.response_status < 300
                  ? 'text-green-600'
                  : log.response_status
                    ? 'text-red-600'
                    : 'text-gray-400'
              }`}>
                {log.response_status || '-'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Tempo</p>
              <p className="text-sm font-mono font-medium text-gray-900">
                {executionLogService.formatExecutionTime(log.execution_time_ms)}
              </p>
            </div>
          </div>

          {log.error_message && (
            <div className="px-6 py-3 bg-red-50 border-b border-red-100">
              <p className="text-xs text-red-600 uppercase tracking-wider mb-1">Erro</p>
              <p className="text-sm text-red-700">{log.error_message}</p>
            </div>
          )}

          {(log.user_name || log.user_email || log.user_guid) && (
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-100">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Usuario</p>
              <p className="text-sm font-medium text-gray-900">
                {log.user_name || 'Usuario nao identificado'}
              </p>
              {log.user_email && (
                <p className="text-sm text-gray-600 mt-0.5">{log.user_email}</p>
              )}
              {!log.user_name && !log.user_email && log.user_guid && (
                <p className="text-xs font-mono text-gray-500 mt-1">{log.user_guid}</p>
              )}
            </div>
          )}

          <div className="px-6 py-4">
            <div className="flex items-center gap-2 mb-4 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('request')}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'request'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <ArrowUpRight className="w-4 h-4" />
                Request Payload
              </button>
              <button
                onClick={() => setActiveTab('response')}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'response'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <ArrowDownLeft className="w-4 h-4" />
                Response
              </button>
            </div>

            <div className="relative">
              <button
                onClick={() => copyToClipboard(
                  formatJson(activeTab === 'request' ? log.request_payload : log.response_data),
                  activeTab
                )}
                className="absolute top-2 right-2 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title="Copiar"
              >
                {copiedField === activeTab ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
              <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-auto max-h-80 text-sm font-mono">
                {formatJson(activeTab === 'request' ? log.request_payload : log.response_data)}
              </pre>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
